import { type KeyboardEvent, useEffect, useRef } from 'react'
import {
  faAnglesDown,
  faAnglesUp,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'

import { Icon, IconButton } from '../../shared/components'
import { useT } from '../../shared/i18n'

import { useNavSections } from './hooks'
import { NavGroup } from './NavGroup'
import type { SidebarNavProps } from './Shell.types'

/**
 * The menu: a filter box, expand/collapse all, and one accordion section per
 * group (Content, every plugin, Build, Administration). "/" focuses the
 * filter; Escape clears it.
 */
export function SidebarNav({ sections, rail }: SidebarNavProps) {
  const { t } = useT()
  const nav = useNavSections(sections)
  const input = useRef<HTMLInputElement>(null)

  // "/" anywhere outside a text field jumps to the filter.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const el = e.target as HTMLElement | null

      if (
        e.key !== '/' ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        el?.closest('input, textarea, select, [contenteditable="true"]')
      )
        return
      e.preventDefault()
      input.current?.focus()
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && nav.filter) {
      e.preventDefault()
      nav.setFilter('')
    }
  }

  return (
    <nav className="sidebar__nav">
      {!rail && (
        <div className="sidebar__tools">
          <label className="sidebar__filter">
            <Icon icon={faMagnifyingGlass} />
            <span className="sr-only">{t.shell.filterMenu}</span>
            <input
              ref={input}
              type="search"
              value={nav.filter}
              placeholder={t.shell.filterMenu}
              onChange={(e) => nav.setFilter(e.target.value)}
              onKeyDown={onKeyDown}
              aria-keyshortcuts="/"
            />
          </label>
          <IconButton
            icon={nav.allOpen ? faAnglesUp : faAnglesDown}
            label={nav.allOpen ? t.shell.collapseAll : t.shell.expandAll}
            onClick={() => nav.setAll(!nav.allOpen)}
            disabled={nav.filtering}
          />
        </div>
      )}
      {nav.sections.map((s) => (
        <NavGroup
          key={s.id}
          section={s}
          rail={rail}
          open={nav.isOpen(s.id)}
          active={!nav.filtering && s.id === nav.activeId}
          onToggle={() => nav.toggle(s.id)}
        />
      ))}
      {nav.filtering && !nav.sections.length && (
        <p className="sidebar__empty" role="status">
          {t.shell.noMenuMatches}
        </p>
      )}
    </nav>
  )
}
